import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { imgUrl } from "@/lib/api";
import PincodeChecker from "@/components/PincodeChecker";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, deliveryFee, total, pincodeInfo } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="overline">Your basket</div>
        <h1 className="font-display text-4xl md:text-5xl mt-3">Nothing here yet.</h1>
        <p className="mt-3 text-[#6C665F]">Pick a cake, a hamper, or a bouquet - we'll pack it with love.</p>
        <Link to="/shop/cakes" data-testid="cart-empty-cta" className="btn-primary mt-8 inline-flex">Browse Cakes</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2">
        <div className="overline">Your basket</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Review your gifts</h1>
        <div className="mt-8 space-y-6" data-testid="cart-items">
          {items.map((it, i) => (
            <div key={i} className="flex gap-4 border-b border-[#E5DFD3] pb-6" data-testid={`cart-item-${i}`}>
              <div className="w-24 h-28 md:w-28 md:h-32 rounded-xl overflow-hidden bg-[#F4F2EC] shrink-0">
                <img src={imgUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between gap-4">
                  <div>
                    <div className="font-display text-xl">{it.name}</div>
                    {it.weight && <div className="text-sm text-[#6C665F]">{it.weight}</div>}
                    {it.message && <div className="text-sm italic text-[#6C665F] mt-1">"{it.message}"</div>}
                  </div>
                  <div className="font-semibold">₹{it.price * it.quantity}</div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="inline-flex items-center border border-[#E5DFD3] rounded-full">
                    <button onClick={() => updateQty(i, it.quantity - 1)} data-testid={`cart-dec-${i}`} className="p-2"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="px-3 text-sm" data-testid={`cart-qty-${i}`}>{it.quantity}</span>
                    <button onClick={() => updateQty(i, it.quantity + 1)} data-testid={`cart-inc-${i}`} className="p-2"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => removeItem(i)} data-testid={`cart-remove-${i}`} className="text-[#6C665F] hover:text-[#8C3A3A] text-sm inline-flex items-center gap-1">
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <aside className="lg:sticky lg:top-24 h-fit">
        <PincodeChecker compact />
        <div className="mt-6 card-eko p-6">
          <div className="overline mb-4">Order Summary</div>
          <div className="flex justify-between text-sm py-2">
            <span>Subtotal</span><span data-testid="summary-subtotal">₹{subtotal}</span>
          </div>
          <div className="flex justify-between text-sm py-2">
            <span>Delivery</span>
            <span data-testid="summary-delivery">{pincodeInfo?.deliverable ? `₹${deliveryFee}` : "Enter pincode"}</span>
          </div>
          <div className="border-t border-[#E5DFD3] mt-3 pt-3 flex justify-between text-lg font-semibold">
            <span>Total</span><span data-testid="summary-total">₹{total}</span>
          </div>
          <button
            data-testid="checkout-btn"
            disabled={!pincodeInfo?.deliverable}
            onClick={() => navigate("/checkout")}
            className="btn-primary w-full mt-6 justify-center"
          >
            {pincodeInfo?.deliverable ? "Proceed to Checkout" : "Enter Pincode to Continue"}
          </button>
        </div>
      </aside>
    </div>
  );
}
